/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  useContext,
  FC,
  useState,
  useReducer,
  useMemo,
  PropsWithChildren,
} from 'react';

import { Schedule } from '../../types';

export const ACTIONS = {
  UPDATE_COLLAPSED_ROWS: 'UPDATE_COLLAPSED_ROWS',
  UPDATE_SCHEDULES: 'UDPATE_SCHEDULES',
  UPDATE_SCHEDULES_ERROR: 'UPDATE_SCHEDULES_ERROR',
  UPDATE_SET_SELECTION: 'UPDATE_SET_SELECTION',
  UPDATE_HIDDEN_COLUMNS: 'UPDATE_HIDDEN_COLUMNS',
  UPDATE_COLUMN_ORDER: 'UPDATE_COLUMN_ORDER',
};

function reducer(
  state: { collapsedRows: Map<string, boolean> },
  action: { type?: string; payload?: any },
) {
  const { payload } = action;
  switch (action.type) {
    case ACTIONS.UPDATE_COLLAPSED_ROWS:
      const collapsedRows = new Map(state.collapsedRows);
      collapsedRows.set(payload.key, payload.value);
      return {
        ...state,
        collapsedRows,
      };
    case ACTIONS.UPDATE_SCHEDULES:
      return { ...state, schedules: payload };
    case ACTIONS.UPDATE_SCHEDULES_ERROR:
      return { ...state, onTryAgain: payload };
    case ACTIONS.UPDATE_SET_SELECTION:
      return { ...state, setSelection: payload };
    case ACTIONS.UPDATE_HIDDEN_COLUMNS:
      return { ...state, hiddenColumns: payload };
    case ACTIONS.UPDATE_COLUMN_ORDER:
      return { ...state, columnOrder: payload };
    default:
      throw new Error();
  }
}

const ScheduleContext = createContext<{
  laborResources: { label: string; value: string }[];
  setLaborResources: (
    laborResources: { label: string; value: string }[],
  ) => void;
  activityCodesMap: Map<string, Set<string>>;
  setActivityCodesMap: (map: unknown) => void;
  collapsedRows: Map<string, boolean>;
  setExpandedRows: (data: unknown) => void;
  dispatch: (data: { type: string; payload?: unknown }) => void;
  setSelection: (...args: unknown[]) => void;
  schedules?: Schedule[];
  onTryAgain: () => void;
  hiddenColumns?: Set<string>;
  columnOrder?: string[];
}>({
  laborResources: [],
  setLaborResources: () => undefined,
  activityCodesMap: new Map(),
  setActivityCodesMap: () => undefined,
  collapsedRows: new Map(),
  setExpandedRows: () => undefined,
  dispatch: () => undefined,
  setSelection: () => undefined,
  schedules: undefined,
  onTryAgain: () => undefined,
  hiddenColumns: undefined,
  columnOrder: undefined,
});

const ScheduleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [laborResources, setLaborResources] = useState([]);
  const [activityCodesMap, setActivityCodesMap] = useState(new Map());
  const [state, dispatch] = useReducer(reducer, {
    collapsedRows: new Map(),
    schedules: undefined,
  });

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
      laborResources,
      setLaborResources,
      activityCodesMap,
      setActivityCodesMap,
    }),
    [
      state,
      laborResources,
      setLaborResources,
      activityCodesMap,
      setActivityCodesMap,
    ],
  );

  return (
    <ScheduleContext.Provider value={value as any}>
      {children}
    </ScheduleContext.Provider>
  );
};

const useScheduleContext = () => useContext(ScheduleContext);

export { useScheduleContext, ScheduleProvider };
